import {
  ChakraFlex,
  DateFormat,
  EquipUIFireflyV2Theme,
  FAIconName,
  FireflyButton,
  FireflyKebabMenu,
  Icon,
  MaudCardFrame,
  ProviderAvatarBlock,
  Tag,
  TagColor,
  Text,
} from '@equip.health/ui';
import { CareTeamTranslations } from '~/lib/constants/careTeam.constants';
import { UserStatus } from '~/lib/constants/user.constants';
import { CareTeamMemberCardProps } from '~/lib/types/careTeam/careTeam';
import { isDateTodayOrInPast } from '~/lib/util/date.util';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { PatientEvents } from '~/analytics/patientEvents';
import { CareTeamMemberCardEditBody } from '~/components/maud/patients/profile/tabs/careplan/CareTeamMemberCardEditBody';
import { DateTime } from 'luxon';
import { getLatestOutOfOfficeEndDate } from '~/lib/util/profile.util';

const {
  neutral: { secondary: neutralSecondary },
} = EquipUIFireflyV2Theme.colors;

const CareTeamMemberCard = ({
  avatarSrc,
  firstName,
  id,
  lastName,
  displayFirstName,
  displayLastName,
  outOfOffice,
  onRemove,
  pronouns,
  role,
  isTemporary = false,
  onIsTemporaryChange,
  textContent,
  credentials,
  endDate,
  isEditMode = false,
  showTags = false,
  userStatus,
  isEndAssignment = false,
  isVoidAssignment = false,
  handleEndAssignmentChange,
  handleVoidAssignmentChange,
  hasTreatmentEpisode = false,
  patientId,
  statusTag,
  kebabMenuOptions,
}: CareTeamMemberCardProps) => {
  const hasAssignmentEnded = endDate && isDateTodayOrInPast(endDate);

  const isInactive = userStatus === UserStatus.INACTIVE;

  const { trackEvent } = useAnalytics();

  const shouldShowAsInactive =
    isInactive || isEndAssignment || hasAssignmentEnded || isVoidAssignment;

  const shouldShowDisplayName = displayFirstName && displayLastName;
  const oooEndDate = getLatestOutOfOfficeEndDate(outOfOffice);

  return (
    <MaudCardFrame
      id={`care-team-member__user-${id}`}
      isDisabled={shouldShowAsInactive}
    >
      <ChakraFlex
        alignItems="flex-start"
        justifyContent="space-between"
        width="100%"
      >
        <ChakraFlex flexDirection="column" gap="15px" width="100%">
          <ProviderAvatarBlock
            avatarUrl={avatarSrc}
            credentials={credentials}
            deactivatedDate={
              isInactive &&
              endDate &&
              DateTime.fromISO(endDate).toFormat(DateFormat.shortMonthDayYear)
            }
            displayName={
              shouldShowDisplayName && {
                firstName: displayFirstName,
                lastName: displayLastName,
              }
            }
            id={`care-team-member_avatar-${id}`}
            isInactiveUser={shouldShowAsInactive}
            legalName={{ firstName, lastName }}
            oooDate={oooEndDate}
            pronouns={pronouns}
            providerTypeName={role}
            shouldShowCredentials
            shouldShowDeactivatedDate={isInactive}
            shouldShowOOOIcon={!!oooEndDate}
            shouldShowPronouns
            shouldShowProviderType
            variant="standard"
          />
          {!isEditMode && textContent && (
            <ChakraFlex
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <ChakraFlex alignItems="center" gap="10px">
                <Icon name={FAIconName.calendarLinesRegular} />
                <Text variant="caption">{textContent}</Text>
              </ChakraFlex>
              <ChakraFlex alignItems="center" gap="8px">
                {showTags && isTemporary && (
                  <Tag
                    color={TagColor.orange}
                    id={`care-team-member__user-temporary-tag-${id}`}
                    size="sm"
                    value={CareTeamTranslations.TEMPORARY}
                  />
                )}
                {showTags && statusTag && (
                  <Tag
                    color={statusTag.color}
                    id={statusTag.id}
                    size="sm"
                    value={statusTag.value}
                  />
                )}
              </ChakraFlex>
            </ChakraFlex>
          )}
        </ChakraFlex>
        {!isEditMode && kebabMenuOptions && kebabMenuOptions.length > 0 && (
          <ChakraFlex
            alignItems="center"
            flexShrink={0}
            marginRight="-8px"
            marginTop="-4px"
            onClick={(e) => e.stopPropagation()}
          >
            <FireflyKebabMenu
              aria-label="Care team member actions"
              options={kebabMenuOptions}
            />
          </ChakraFlex>
        )}
        {isEditMode && (
          <CareTeamMemberCardEditBody
            firstName={firstName}
            handleEndAssignmentChange={handleEndAssignmentChange}
            handleVoidAssignmentChange={handleVoidAssignmentChange}
            hasTreatmentEpisode={hasTreatmentEpisode}
            id={id}
            isTemporary={isTemporary}
            lastName={lastName}
            onIsTemporaryChange={onIsTemporaryChange}
            onRemove={onRemove}
            patientId={patientId}
          />
        )}
        {isEndAssignment && handleEndAssignmentChange && (
          <>
            <Text
              color={neutralSecondary}
              marginRight="36px"
              size="15px"
              variant="caption"
              whiteSpace="nowrap"
            >
              {CareTeamTranslations.MARKED_TO_END}
            </Text>
            <FireflyButton
              aria-label={`Undo end assignment for ${firstName} ${lastName}`}
              data-testid={`care-team-member__end-assignment-button-${id}`}
              id={`care-team-member__end-assignment-button-${id}`}
              leftIcon={FAIconName.undoRegular}
              onClick={() => {
                handleEndAssignmentChange?.(id);
                trackEvent(PatientEvents.providerAssignmentUndoButtonClicked, {
                  patientId,
                  source: AnalyticsSourceValues.CARE_PLAN_TAB,
                });
              }}
              variant="tertiary"
            >
              {CareTeamTranslations.UNDO}
            </FireflyButton>
          </>
        )}
        {isVoidAssignment && handleVoidAssignmentChange && (
          <>
            <Text
              color={neutralSecondary}
              marginRight="36px"
              size="15px"
              variant="caption"
              whiteSpace="nowrap"
            >
              {CareTeamTranslations.MARKED_TO_VOID}
            </Text>
            <FireflyButton
              aria-label={`Undo void assignment for ${firstName} ${lastName}`}
              data-testid={`care-team-member__void-assignment-button-${id}`}
              id={`care-team-member__void-assignment-button-${id}`}
              leftIcon={FAIconName.undoRegular}
              onClick={() => handleVoidAssignmentChange?.(id)}
              variant="tertiary"
            >
              {CareTeamTranslations.UNDO}
            </FireflyButton>
          </>
        )}
      </ChakraFlex>
    </MaudCardFrame>
  );
};

export default CareTeamMemberCard;
